import React, { useState, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  IconButton,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, getHeader } from "../../utils/util";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_ENDPOINT } from "../../utils/constants";

const ResellerDetail = () => {
  const { id } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountToConfirm, setAccountToConfirm] = useState(null);
  const [assignedAccounts, setAssignedAccounts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/user/get-reseller/${id}`
  );

  const { data: customersData, isLoading: customersLoading } =
    useApiEp(`/api/customer-ids`);

  const customers = customersData || [];

  useEffect(() => {
    if (data?.accounts) {
      setAssignedAccounts(data.accounts);
    }
  }, [data?.accounts]);

  // Filter out already assigned accounts from dropdown
  const availableAccounts = customers.filter(
    (customer) => !assignedAccounts.includes(customer)
  );

  const handleAddAccount = async () => {
    if (!selectedAccount) return;

    setAdding(true);
    try {
      const response = await fetch(
        `${API_ENDPOINT}/api/add-account-to-reseller/${id}`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({
            customerId: selectedAccount,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setAssignedAccounts([...assignedAccounts, selectedAccount]);
        setSnackbar({
          open: true,
          message: result.message || "Account added successfully",
          severity: "success",
        });
        setSelectedAccount(null);
        setOpenDialog(false);
        refetch();
      } else {
        throw new Error(result.message || "Failed to add account");
      }
    } catch (err) {
      console.error("Failed to add account:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to add account",
        severity: "error",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAccount = async (accountToRemove) => {
    setRemoving(accountToRemove);
    try {
      const response = await fetch(
        `${API_ENDPOINT}/api/remove-account-from-reseller/${id}`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify({ customerId: accountToRemove }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setAssignedAccounts(
          assignedAccounts.filter((acc) => acc !== accountToRemove)
        );
        setSnackbar({
          open: true,
          message: result.message || "Account removed successfully",
          severity: "success",
        });
        refetch();
      } else {
        throw new Error(result.message || "Failed to remove account");
      }
    } catch (err) {
      console.error("Failed to remove account:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to remove account",
        severity: "error",
      });
    } finally {
      setRemoving(null);
    }
  };

  const handleConfirmRemove = () => {
    if (!accountToConfirm) return;
    setOpenConfirm(false);
    handleRemoveAccount(accountToConfirm);
    setAccountToConfirm(null);
  };

  if (isLoading) return <MuPb />;

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Paper elevation={3} sx={{ mt: 2, p: 3, borderRadius: 2 }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {data.companyName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reseller ID: {data._id}
            </Typography>
          </Box>

          <Chip
            label={data.isActive ? "Active" : "Inactive"}
            color={data.isActive ? "success" : "error"}
            variant="outlined"
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Details */}
        <Stack spacing={2}>
          <DetailRow label="Username" value={data.username} />
          <DetailRow
            label="Full Name"
            value={`${data.firstName} ${data.lastName}`}
          />
          <DetailRow label="User Type" value={data.userType} />
          <DetailRow label="Email" value={data.email} />
          <DetailRow label="Password" value={data.password} />
          <DetailRow
            label="Created On"
            value={new Date(data.createdOn).toLocaleString()}
          />
          <DetailRow
            label="Last Updated"
            value={new Date(data.updatedOn).toLocaleString()}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Accounts Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            Assigned Accounts ({assignedAccounts.length})
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon />}
            disabled={customersLoading || availableAccounts.length === 0}
          >
            Add Account
          </Button>
        </Box>

        {assignedAccounts.length === 0 ? (
          <Alert severity="info">No accounts assigned to this reseller.</Alert>
        ) : (
          <Stack spacing={1}>
            {assignedAccounts.map((account) => (
              <Paper
                key={account}
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Typography variant="body1">{account}</Typography>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    setAccountToConfirm(account);
                    setOpenConfirm(true);
                  }}
                  disabled={removing === account}
                >
                  {removing === account ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DeleteIcon />
                  )}
                </IconButton>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Add Account Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => !adding && setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Account to Reseller</DialogTitle>
          <DialogContent>
            <Autocomplete
              options={availableAccounts}
              getOptionLabel={(option) => option}
              value={selectedAccount}
              onChange={(event, newValue) => setSelectedAccount(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select customer"
                  fullWidth
                  sx={{ mt: 2 }}
                  placeholder="Search customers..."
                />
              )}
              loading={customersLoading}
              disabled={adding}
            />
            {availableAccounts.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                All available accounts have been assigned.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={adding}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAccount}
              variant="contained"
              disabled={!selectedAccount || adding}
              startIcon={adding && <CircularProgress size={16} />}
            >
              {adding ? "Adding..." : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Remove Dialog */}
        <Dialog
          open={openConfirm}
          onClose={() => !removing && setOpenConfirm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Remove Account</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove "{accountToConfirm}" from this
              reseller? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirm(false);
                setAccountToConfirm(null);
              }}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              variant="contained"
              color="error"
              disabled={removing}
              startIcon={removing && <CircularProgress size={16} />}
            >
              {removing ? "Removing..." : "Remove"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

/* ------------------ Reusable Row ------------------ */
const DetailRow = ({ label, value }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || "-"}
    </Typography>
  </Stack>
);

export default ResellerDetail;
